import React from "react";
import { ADVERB, FitView, anatomical, fmt, mm2, shade } from "./SlitLampFit";
import "./SimulatedFit.css";

/**
 * How the lens sits once it settles, seen at the slit lamp: the lens in Lens
 * Specs beside the program's own order from the scan levelled to its cornea.
 *
 * Everything drawn comes from the backend's "Simulated Fit" (version 3): a
 * rigid lens tipped about its landing and lowered until the landing sits on
 * the eye as evenly as it can, on the levelled scan. Nothing here recomputes
 * the lens.
 */

export { anatomical, landing, settleSummary } from "./SlitLampFit";

function sagText(sag) {
  if (!sag) return "—";
  if (sag.flat != null) return `${fmt(sag.flat)} flat / ${fmt(sag.steep)} steep`;
  return fmt(sag.single);
}

function dotsText(fit, side) {
  if (!fit.dots || !fit.dots.length) return "—";
  const near = fit.dots.reduce((a, b) => (b.clearance < a.clearance ? b : a));
  const far = fit.dots.reduce((a, b) => (b.clearance > a.clearance ? b : a));
  return `${mm2(near.clearance)} ${anatomical(near.angle, side)} to ${mm2(far.clearance)} mm ${anatomical(far.angle, side)}`;
}

function bielevationText(fit) {
  if (!fit.sag || fit.sag.flat == null) return "none";
  return `${fmt(fit.sag.steep - fit.sag.flat)} µm on ${Math.round(fit.steep % 180)}°`;
}

function limbalText(fit) {
  const l = fit.limbal;
  if (!l) return "—";
  const axis = l["steep axis"] == null ? "" : `, steep ${Math.round(l["steep axis"])}°`;
  return `${fmt(l["meridian difference"])} µm${axis}`;
}

function Comparison({ a, b, side, dotTarget }) {
  const tilt = (f) => (f.tilt_deg < 0.3 || f.dips_toward == null ? "level" : `${f.tilt_deg.toFixed(1)}° into the ${anatomical(f.dips_toward, side)} side`);
  const shift = (f) => (f.optic_toward == null || f.optic_shift_mm < 0.05
    ? "centred"
    : `${f.optic_shift_mm.toFixed(2)} mm ${ADVERB[anatomical(f.optic_toward, side)] || anatomical(f.optic_toward, side)}`);
  const dotsOff = (f) => Boolean(f.dots && f.dots.some((d) => d.clearance < dotTarget[0] || d.clearance > dotTarget[1]));
  const clearanceOff = (f) => Math.abs(f.clearance_under_apex - f.clearance_target) > 50;
  const rows = [
    ["Quadrant steps", (f) => f.steps.map((q) => q.steps).join(" ")],
    ["Sag", (f) => sagText(f.sag)],
    ["Toricity", (f) => `F ${f.toric.F} / S ${f.toric.S}`],
    ["Base curve", (f) => (typeof f.base_curve === "number" ? f.base_curve.toFixed(2) : "—")],
    ["Bi-elevation", bielevationText],
    ["Tips", tilt],
    ["Optic zone off the apex", shift],
    ["Under the lens apex", (f) => `${fmt(f.clearance_under_apex)} µm (aim ${f.clearance_target})`, clearanceOff],
    ["Drill dots outside the limbus", (f) => dotsText(f, side), dotsOff],
    ["Limbal toricity read", limbalText],
  ];
  return (
    <table className="sf-compare">
      <thead><tr><th scope="col" /><th scope="col">Lens Specs</th><th scope="col">Levelled scan</th></tr></thead>
      <tbody>
        {rows.map(([label, value, warn]) => {
          const x = value(a), y = value(b);
          return (
            <tr key={label} className={x !== y ? "sf-differs" : undefined}>
              <th scope="row">{label}</th>
              <td className={warn && warn(a) ? "sf-warn" : undefined}>{x}</td>
              <td className={warn && warn(b) ? "sf-warn" : undefined}>{y}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function Key() {
  const ramp = (kind) => (
    <span className="sf-ramp">
      {[1, 2, 3].map((level) => <i key={level} style={{ background: shade({ kind, level }) }} />)}
    </span>
  );
  return (
    <dl className="sf-key">
      <dt>Landing</dt>
      <dd>
        <span>{ramp("lift")}lifts: the edge stands off</span>
        <span><span className="sf-sw" style={{ background: shade({ kind: "aligned" }) }} />aligned, within one 30 µm step</span>
        <span>{ramp("comp")}compresses: the edge presses in</span>
        <span className="sf-key-quiet">darker = more steps (1–2, 3–5, 6+)</span>
      </dd>
      <dt>Limbus</dt>
      <dd>
        <span><i className="sf-k-limbus" />traced by the Pentacam (dots), and the circle through it</span>
        <span><i className="sf-k-axis" />its steep meridian</span>
        <span><i className="sf-k-be" />the order's bi-elevation</span>
      </dd>
      <dt>Lens</dt>
      <dd>
        <span><i className="sf-k-zone" />limbal clearance zone</span>
        <span><i className="sf-k-dot sf-k-in" />drill dot 0–0.5 mm outside the limbus</span>
        <span><i className="sf-k-dot" />outside that range (mm)</span>
        <span><b className="sf-k-apex">+</b>corneal apex, <i className="sf-k-optic" />optic zone centre; the arrow shows which way it moved</span>
      </dd>
    </dl>
  );
}

export default function SimulatedFit({ sim, diameter }) {
  const ready = sim && sim.version >= 3 && Array.isArray(sim.options);
  const option = ready ? sim.options.find((o) => o.diameter === diameter) || sim.options.find((o) => o.recommended) : null;
  const side = ready ? sim.eye : null;
  const limbus = ready ? sim.limbus_levelled : null;
  const dotTarget = ready && sim.dot_target_mm ? sim.dot_target_mm : [0, 0.5];
  const limbusLine = limbus
    ? `The limbus as traced is ${limbus.d.toFixed(2)} mm across, its centre ${Math.hypot(limbus.cx, limbus.cy).toFixed(2)} mm ${
        anatomical((Math.atan2(limbus.cy, limbus.cx) * 180) / Math.PI, side)} of the corneal apex.`
    : null;

  return (
    <div className="sf">
      <div className="sf-head">
        <h2 className="sf-title">Simulated Fit</h2>
        {option && <span className="sf-dia">{option.diameter.toFixed(1)} mm</span>}
      </div>

      {!ready && (
        <p className="sf-note">
          {sim && sim.error ? sim.error : "Not available for this result: it was saved before the simulated fit was added. Upload the export again to see it."}
        </p>
      )}

      {option && (
        <>
          <p className="sf-lede">
            The lens as you would see it at the slit lamp once it settles: a rigid lens tips about its landing until the
            landing rests as evenly as it can, carrying its optic zone toward the side that dips. First the lens in Lens
            Specs, then your program's own order from the scan levelled to its cornea
            {sim.tilt_deg >= 0.3 ? ` (it was tilted ${sim.tilt_deg.toFixed(1)}° in the instrument)` : ""}, settled the same way.
          </p>

          <div className="sf-views">
            <FitView title="Lens Specs" what="the lens above, settled on this eye" fit={option.ordered}
                  option={option} limbus={limbus} side={side} dotTarget={dotTarget} />
            <FitView title="Levelled scan" what="the program's order from the levelled scan" fit={option.levelled}
                  option={option} limbus={limbus} side={side} dotTarget={dotTarget} />
          </div>

          {option.ordered && option.levelled && <Comparison a={option.ordered} b={option.levelled} side={side} dotTarget={dotTarget} />}
          {limbusLine && <p className="sf-note sf-limbus">{limbusLine}</p>}

          <Key />

          <details className="sf-method">
            <summary>How this is worked out</summary>
            <p><strong>The eye</strong> is the scan levelled to its cornea: the tilt of the central cornea is measured and the whole map rotated about the apex until it is level. Both lenses are settled on it.</p>
            <p><strong>The lens</strong> is the base curve out to the midperipheral zone, and a landing from the limbal clearance curve to the edge that follows the sclera's average profile and reaches the ordered sag, with the bi-elevation and the quadrant steps blended smoothly between quadrant centres. The limbal clearance curve's own shape is not in the export, so no limbal clearance is shown.</p>
            <p><strong>Settling.</strong> The lens is rigid. It tips about the centre of its landing and lowers until the landing sits on the sclera as evenly as it can. A lens that only slid would move toward the higher side; lenses are seen to decentre toward the deeper side, which is what tipping does. The landing barely moves; the optic zone, some 4–5 mm above it, swings. Lid forces and blinking are not modelled.</p>
            <p><strong>Landing</strong> is judged on the outer half millimetre of the landing, where the quadrant steps take full effect, averaged across each quadrant, in 30 µm steps. The drill dots are drawn evenly spaced, where the landing begins; the guide asks for 0–0.5 mm outside the limbus. Nasal and temporal take the map as the examiner's view, as the Pentacam displays it.</p>
          </details>
        </>
      )}
    </div>
  );
}
